import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Check, ChevronDown, Plus, Store } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isNativeApp } from "@/lib/native/platform";
import { listRememberedVendors, vendorIcon, type RememberedVendor } from "@/lib/native/vendorMemory";

// Persistent vendor identity bar (TestFlight round-1 feedback). Two jobs:
//
// 1. BRANDING — the ordering surface otherwise "just looks like FreshOrder";
//    the vendor's own logo + name anchor every customer page so a buyer always
//    knows whose store they're in.
// 2. VENDOR MENU — tapping the identity always opens a menu (TestFlight
//    2026-09-06: with one vendor there was no way to reach "Add a vendor"
//    without relaunching the app). With one vendor the menu is just that
//    vendor + "Add a vendor"; with two or more it becomes the switcher, and a
//    one-time coach mark introduces the switching the first time it becomes
//    possible. Works on the web too (menu = current vendor + add) so the
//    behavior is consistent for buyers who use multiple vendors in a browser.
//
// Vendor memory is native-only (device-local Capacitor Preferences), so on the
// web the menu lists only the current vendor.

const HINT_KEY = "fo_vendor_switch_hint_shown";

interface VendorTopBarProps {
  orgSlug?: string;
  orgName?: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
}

export function VendorTopBar({ orgSlug, orgName, logoUrl, faviconUrl }: VendorTopBarProps) {
  const [, setLocation] = useLocation();
  const [vendors, setVendors] = useState<RememberedVendor[]>([]);
  const [hintVisible, setHintVisible] = useState(false);

  useEffect(() => {
    if (!isNativeApp()) return;
    listRememberedVendors().then(setVendors);
  }, [orgSlug]);

  const multiVendor = vendors.length >= 2;

  // One-time coach mark, armed the first time SWITCHING becomes possible (2+
  // vendors). The single-vendor menu needs no tutorial — it's one tap, one item.
  useEffect(() => {
    if (!(isNativeApp() && multiVendor)) return;
    try {
      if (!localStorage.getItem(HINT_KEY)) setHintVisible(true);
    } catch {
      /* storage unavailable — skip the hint, never the bar */
    }
  }, [multiVendor]);

  const dismissHint = () => {
    setHintVisible(false);
    try {
      localStorage.setItem(HINT_KEY, "1");
    } catch {
      /* best-effort */
    }
  };

  if (!orgName) return null;
  // Prefer the SQUARE mark (favicon) — the org icon the admin dashboard uses. logoUrl is
  // typically a wide logo+wordmark lockup that letterboxes to a strip in a 28px tile.
  const logo = faviconUrl || logoUrl || null;

  // Menu rows: the remembered vendors (native), with the CURRENT org prepended when it
  // isn't among them (always the case on the web, where vendor memory is empty).
  const currentInList = !!orgSlug && vendors.some((v) => v.slug === orgSlug);
  const menuVendors: RememberedVendor[] =
    currentInList || !orgSlug
      ? vendors
      : [{ slug: orgSlug, orgName, iconUrl: faviconUrl, logoUrl, lastUsedAt: "" }, ...vendors];

  const identity = (
    <span className="flex items-center gap-2 min-w-0">
      {/* The tile carries the ground, not the <img>: bg-white on an image triggers
          index.css's width:100%!important mobile rule and blows the logo across the row. */}
      <span
        className={`h-7 w-7 shrink-0 overflow-hidden rounded-md flex items-center justify-center ${
          logo ? "bg-[#fff] border border-gray-200" : "bg-[#467B5A]/10"
        }`}
      >
        {logo ? (
          <img src={logo} alt="" className="max-h-full max-w-full object-contain" />
        ) : (
          <Store className="h-4 w-4 text-[#467B5A]" />
        )}
      </span>
      <span className="text-sm font-semibold text-gray-900 truncate">{orgName}</span>
    </span>
  );

  return (
    <div className="relative flex items-center px-3 pt-2 lg:hidden" data-testid="vendor-top-bar">
      <DropdownMenu onOpenChange={(open) => { if (open) dismissHint(); }}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 min-w-0 -mx-1.5 rounded-lg px-1.5 py-1 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            data-testid="vendor-switcher-trigger"
          >
            {identity}
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {menuVendors.map((v) => (
            <DropdownMenuItem
              key={v.slug}
              className="gap-2.5"
              onSelect={() => {
                if (v.slug === orgSlug) return;
                setLocation(v.customUrl ? `/org/${v.slug}/order/${v.customUrl}` : `/org/${v.slug}`);
              }}
              data-testid={`vendor-switch-${v.slug}`}
            >
              <span
                className={`h-6 w-6 shrink-0 overflow-hidden rounded flex items-center justify-center ${
                  vendorIcon(v) ? "bg-[#fff] border border-gray-200" : "bg-[#467B5A]/10"
                }`}
              >
                {vendorIcon(v) ? (
                  <img src={vendorIcon(v)!} alt="" className="max-h-full max-w-full object-contain" />
                ) : (
                  <Store className="h-3.5 w-3.5 text-[#467B5A]" />
                )}
              </span>
              <span className="flex-1 truncate text-sm">{v.orgName || v.slug}</span>
              {v.slug === orgSlug && <Check className="h-4 w-4 shrink-0 text-[#3A6247]" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="gap-2.5" onSelect={() => setLocation("/app")} data-testid="vendor-switch-add">
            <span className="h-6 w-6 shrink-0 rounded bg-gray-100 flex items-center justify-center">
              <Plus className="h-3.5 w-3.5 text-gray-600" />
            </span>
            <span className="text-sm">Add a vendor</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {hintVisible && (
        <button
          type="button"
          onClick={dismissHint}
          className="absolute left-3 top-full z-[70] mt-1 rounded-lg bg-gray-900/95 px-3 py-2 text-left text-xs font-medium text-white shadow-lg"
          data-testid="vendor-switch-hint"
        >
          You have more than one supplier now — tap the logo to switch.
        </button>
      )}
    </div>
  );
}
